import { useState } from "react"
import { DoorOpen } from "lucide-react"
import { EntryRequestActions } from "@/features/entry-requests"
import { useDinerRealtime } from "./diner-realtime"

/**
 * Mirrors `Location::EntryRequest::PENDING_TTL`. The feed only ever emits the
 * request's creation — `Notifications::EventMapper` maps it on `create_record`
 * and there is no resolution event to clear it — so the alert has to age out on
 * the same clock the record does. Without this it would sit on every diner
 * screen forever after the person was already waved in.
 */
const PENDING_TTL_MS = 15 * 60 * 1000

/**
 * "Someone is standing at the table asking to be let in."
 *
 * This used to live inside the notifications dropdown, which is gone. It is not
 * a notification in the general sense and it never belonged behind a click: a
 * person is waiting in front of a QR that will not budge until somebody answers,
 * and the whole point of letting a seated diner answer is that a late friend
 * does not need a waiter to walk over.
 *
 * Whether this particular diner may actually say yes is decided server-side by
 * `Location::EntryRequest#approvable_by?` — the buttons being on screen never
 * means the answer will be accepted.
 */
export function EntryRequestAlert() {
  const { notifications } = useDinerRealtime()
  const [answered, setAnswered] = useState<number[]>([])

  const pending = notifications.filter((notification) => {
    if (notification.kind !== "entry_requested") return false
    if (answered.includes(notification.aggregateId)) return false

    const age = Date.now() - new Date(notification.occurredAt).getTime()
    return age >= 0 && age < PENDING_TTL_MS
  })

  if (pending.length === 0) return null

  return (
    <div className="shrink-0 space-y-2 px-4 pt-3" data-testid="entry-request-alerts">
      {pending.map((notification) => (
        <div
          key={notification.id}
          data-testid={`entry-request-alert-${notification.aggregateId}`}
          className="flex items-start gap-3 rounded-xl border border-warning/40 bg-warning/10 p-3"
        >
          <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-background text-warning">
            <DoorOpen className="size-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-tight tracking-tight">
              {notification.title}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">{notification.message}</p>
            <EntryRequestActions
              entryRequestId={notification.aggregateId}
              approveHref={`/entry_requests/${notification.aggregateId}/approve`}
              rejectHref={`/entry_requests/${notification.aggregateId}/reject`}
              onResolved={() =>
                setAnswered((current) => [...current, notification.aggregateId])
              }
            />
          </div>
        </div>
      ))}
    </div>
  )
}
